import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    getSocietyDropdown,
    getEmployeeDirectory,
    getEmployeeStats,
    getEmployeeTypesDropdown,
    getEmployeeDetails,
    createEmployee,
    updateEmployee,
    bulkStatusEmployee,
    bulkReassignEmployee,
} from '../../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction';
import EmployeeFormModal from './EmployeeFormModal';
import AddFromExistingModal from './AddFromExistingModal';
import BulkReassignModal from './BulkReassignModal';
import EmployeeSocietiesModal from './EmployeeSocietiesModal';
import EmployeeStatsCards from './EmployeeStatsCards';
import ChangePasswordModal from './ChangePasswordModal';
import webTokenStorer from '../../../../utils/webStorer';
import {
    DEPARTMENT_OPTIONS,
    EMPLOYMENT_TYPE_OPTIONS,
    employeeFullName,
} from './employeeConstants';

const DEFAULT_LIMIT = 10;

const personKey = (p) => (p.user_id != null ? `u${p.user_id}` : `e${p.employee_id}`);
const personEmployeeIds = (p) => ((p.societies && p.societies.length)
    ? p.societies.map((s) => s.employee_id)
    : [p.employee_id]);
const rowActive = (s) => s?.is_active === 1 || s?.is_active === true;
const activeCount = (p) => (p.societies || []).filter(rowActive).length;
const initials = (p) => employeeFullName(p).split(' ').filter((w) => /[a-z]/i.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

// An employee can belong to several communities, each with its own active flag.
// Spell that out instead of a bare "1/1": fully active, fully inactive, or partial.
const renderStatus = (active, total) => {
    if (!total) return <span className="text-xs text-secondary-light">—</span>;
    if (active === total) {
        return (
            <span className="emp-chip bg-success-focus text-success-main">
                {total === 1 ? 'Active' : `Active in all ${total}`}
            </span>
        );
    }
    if (active === 0) {
        return <span className="emp-chip bg-danger-focus text-danger-main">Inactive</span>;
    }
    return (
        <span className="emp-chip bg-warning-focus text-warning-main"
            title={`Active in ${active} of ${total} communities`}>
            Active in {active} of {total}
        </span>
    );
};

const ManageEmployeeListLayer = () => {
    const dispatch = useDispatch();

    const {
        directoryData, directoryLoading, directoryPagination,
        statsData, statsLoading,
        createLoading, updateLoading, bulkLoading,
        typesDropdownData,
        societyDropdownData,
        detailsData,
    } = useSelector((state) => state.CtrlEmployeeReducer || {});

    // ---- filters / paging ----
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [societyId, setSocietyId] = useState('');
    const [employeeTypeId, setEmployeeTypeId] = useState('');
    const [department, setDepartment] = useState('');
    const [employmentType, setEmploymentType] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    // ---- selection (person-level) ----
    const [selectedKeys, setSelectedKeys] = useState([]);

    // ---- modals ----
    const [formState, setFormState] = useState({ open: false, mode: 'add', employee: null });
    const [addExistingOpen, setAddExistingOpen] = useState(false);
    const [addExistingEmail, setAddExistingEmail] = useState('');
    const [reassignOpen, setReassignOpen] = useState(false);
    const [managePerson, setManagePerson] = useState(null);
    const [pendingEdit, setPendingEdit] = useState(null); // { id, role }
    const [passwordPerson, setPasswordPerson] = useState(null);

    // Changing your own password here would log you out — use /auth/change-password instead.
    const selfUserId = webTokenStorer.getSessionUserId();

    const persons = Array.isArray(directoryData) ? directoryData : [];
    const totalRecords = directoryPagination?.total ?? persons.length;

    const societyOptions = useMemo(() => {
        const rows = Array.isArray(societyDropdownData) ? societyDropdownData : [];
        return rows.map((s) => ({ value: s.value ?? s.society_id, label: s.label ?? s.name }));
    }, [societyDropdownData]);

    const typeOptions = useMemo(() => {
        const rows = Array.isArray(typesDropdownData) ? typesDropdownData : [];
        return rows
            .map((t) => ({ value: t.value ?? t.id ?? t.employee_type_id, label: t.label ?? t.type }))
            .filter((o) => o.value != null && o.label);
    }, [typesDropdownData]);

    useEffect(() => {
        dispatch(getSocietyDropdown());
        dispatch(getEmployeeTypesDropdown());
    }, [dispatch]);

    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const statusToActive = (s) => (s === 'active' ? 1 : s === 'inactive' ? 0 : undefined);

    const fetchDirectory = useCallback(() => {
        dispatch(getEmployeeDirectory({
            page, limit,
            search: search || undefined,
            society_id: societyId || undefined,
            employee_type_id: employeeTypeId || undefined,
            department: department || undefined,
            employment_type: employmentType || undefined,
            is_active: statusToActive(status),
        }));
    }, [dispatch, page, limit, search, societyId, employeeTypeId, department, employmentType, status]);

    const fetchStats = useCallback(() => {
        dispatch(getEmployeeStats({ society_id: societyId || undefined }));
    }, [dispatch, societyId]);

    useEffect(() => { fetchDirectory(); }, [fetchDirectory]);
    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { setSelectedKeys([]); }, [directoryData]);

    // Edit-from-panel: once the full record arrives, open the edit form (merging
    // in the RBAC role from the society row we came from).
    useEffect(() => {
        if (pendingEdit && detailsData && (detailsData.employee_id === pendingEdit.id)) {
            setFormState({ open: true, mode: 'edit', employee: { ...detailsData, role: pendingEdit.role } });
            setPendingEdit(null);
        }
    }, [pendingEdit, detailsData]);

    const refreshAll = () => { fetchDirectory(); fetchStats(); };

    const onFilter = (setter) => (val) => { setter(val); setPage(1); };
    const handleClear = () => {
        setSearchInput(''); setSearch('');
        setSocietyId(''); setEmployeeTypeId(''); setDepartment(''); setEmploymentType(''); setStatus('');
        setPage(1);
    };
    const hasFilters = search || societyId || employeeTypeId || department || employmentType || status;

    // ---- selection ----
    const pageKeys = persons.map(personKey);
    const allSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.includes(k));
    const toggleAll = () => setSelectedKeys(allSelected ? [] : pageKeys);
    const toggleOne = (k) => setSelectedKeys((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
    const selectedEmployeeIds = persons
        .filter((p) => selectedKeys.includes(personKey(p)))
        .flatMap(personEmployeeIds);

    const handleBulkStatus = (makeActive) => {
        if (!selectedEmployeeIds.length) return;
        dispatch(bulkStatusEmployee({
            employee_ids: selectedEmployeeIds,
            is_active: makeActive,
            onSuccess: () => { setSelectedKeys([]); refreshAll(); },
        }));
    };

    const handleBulkReassign = (targetSocietyId) => {
        dispatch(bulkReassignEmployee({
            employee_ids: selectedEmployeeIds,
            society_id: targetSocietyId,
            onSuccess: () => { setReassignOpen(false); setSelectedKeys([]); refreshAll(); },
        }));
    };

    const closeForm = () => setFormState({ open: false, mode: 'add', employee: null });

    const handleFormSubmit = (payload, { isEdit }) => {
        if (isEdit) {
            dispatch(updateEmployee({ ...payload, onSuccess: () => { closeForm(); refreshAll(); } }));
        } else {
            // On error the form stays open (with the user's input) and the API error is toasted.
            dispatch(createEmployee({
                ...payload,
                onSuccess: () => { closeForm(); refreshAll(); },
            }));
        }
    };

    const openManage = (p) => setManagePerson({
        employee_id: p.employee_id,
        user_id: p.user_id,
        first_name: p.first_name,
        last_name: p.last_name,
        email: p.email,
        phone: p.phone,
    });

    // From the societies panel: edit a specific record's details.
    const handleEditRecord = (societyRow) => {
        setManagePerson(null);
        setPendingEdit({ id: societyRow.employee_id, role: societyRow.role });
        dispatch(getEmployeeDetails({ id: societyRow.employee_id }));
    };

    const startIndex = (page - 1) * limit;

    return (
        <>
            <EmployeeStatsCards stats={statsData} loading={statsLoading} />

            <div className="card emp-card">
                {/* Header: title + actions */}
                <div className="emp-head">
                    <div className="d-flex align-items-center gap-10">
                        <span className="emp-head-icon"><Icon icon="solar:users-group-rounded-linear" /></span>
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <h6 className="emp-title">Employee Directory</h6>
                                <span className="emp-count">{totalRecords}</span>
                            </div>
                            <p className="emp-subtitle">Everyone working across your communities</p>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <button onClick={() => { setAddExistingEmail(''); setAddExistingOpen(true); }}
                            className="emp-btn emp-btn-ghost">
                            <Icon icon="mdi:account-search-outline" /> Add From Existing
                        </button>
                        <button onClick={() => setFormState({ open: true, mode: 'add', employee: null })}
                            className="emp-btn emp-btn-primary">
                            <Icon icon="mdi:plus" /> Add Employee
                        </button>
                    </div>
                </div>

                {/* Toolbar: search + filters */}
                <div className="emp-toolbar">
                    <div className="emp-searchbox">
                        <Icon icon="ion:search-outline" className="emp-searchbox-icon" />
                        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name, phone, email, code" />
                        {searchInput && (
                            <button type="button" onClick={() => setSearchInput('')} aria-label="Clear search">
                                <Icon icon="mdi:close" />
                            </button>
                        )}
                    </div>
                    <span className="emp-toolbar-sep" />
                    <div className="emp-filters">
                        <div style={{ width: 170 }}>
                            <SearchableDropdown value={societyId || 'all'}
                                onChange={(v) => onFilter(setSocietyId)(v === 'all' ? '' : v)}
                                options={[{ value: 'all', label: 'All Communities' }, ...societyOptions]}
                                placeholder="Community" clearable={false} small />
                        </div>
                        <div style={{ width: 140 }}>
                            <SearchableDropdown value={employeeTypeId || 'all'}
                                onChange={(v) => onFilter(setEmployeeTypeId)(v === 'all' ? '' : v)}
                                options={[{ value: 'all', label: 'All Types' }, ...typeOptions]}
                                placeholder="Type" clearable={false} small />
                        </div>
                        <div style={{ width: 150 }}>
                            <SearchableDropdown value={department || 'all'}
                                onChange={(v) => onFilter(setDepartment)(v === 'all' ? '' : v)}
                                options={[{ value: 'all', label: 'All Departments' }, ...DEPARTMENT_OPTIONS]}
                                placeholder="Department" clearable={false} small />
                        </div>
                        <div style={{ width: 145 }}>
                            <SearchableDropdown value={employmentType || 'all'}
                                onChange={(v) => onFilter(setEmploymentType)(v === 'all' ? '' : v)}
                                options={[{ value: 'all', label: 'All Employment' }, ...EMPLOYMENT_TYPE_OPTIONS]}
                                placeholder="Employment" clearable={false} small />
                        </div>
                        <div style={{ width: 125 }}>
                            <SearchableDropdown value={status || 'all'}
                                onChange={(v) => onFilter(setStatus)(v === 'all' ? '' : v)}
                                options={[
                                    { value: 'all', label: 'All Status' },
                                    { value: 'active', label: 'Active' },
                                    { value: 'inactive', label: 'Inactive' },
                                ]}
                                placeholder="Status" clearable={false} small />
                        </div>
                        {hasFilters && (
                            <button onClick={handleClear} className="emp-link">
                                <Icon icon="mdi:filter-remove-outline" /> Reset
                            </button>
                        )}
                    </div>
                </div>

                {/* Bulk action bar */}
                {selectedKeys.length > 0 && (
                    <div className="emp-bulk">
                        <span className="emp-bulk-count">
                            <Icon icon="mdi:checkbox-multiple-marked-outline" />
                            {selectedKeys.length} selected
                            <span className="text-secondary-light fw-normal">· {selectedEmployeeIds.length} record(s)</span>
                        </span>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                            <button onClick={() => handleBulkStatus(true)} disabled={bulkLoading} className="emp-btn emp-btn-soft is-success">
                                <Icon icon="mdi:account-check-outline" /> Activate
                            </button>
                            <button onClick={() => handleBulkStatus(false)} disabled={bulkLoading} className="emp-btn emp-btn-soft is-danger">
                                <Icon icon="mdi:account-off-outline" /> Deactivate
                            </button>
                            <button onClick={() => setReassignOpen(true)} disabled={bulkLoading} className="emp-btn emp-btn-soft is-warning">
                                <Icon icon="mdi:office-building-marker-outline" /> Reassign
                            </button>
                        </div>
                        <button onClick={() => setSelectedKeys([])} className="emp-link ms-auto">
                            Clear selection
                        </button>
                    </div>
                )}

                {/* Table */}
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">
                                        <div className="form-check style-check d-flex align-items-center">
                                            <input className="form-check-input" type="checkbox" checked={allSelected} onChange={toggleAll} />
                                        </div>
                                    </th>
                                    <th scope="col">#</th>
                                    <th scope="col">Employee</th>
                                    <th scope="col">Phone</th>
                                    <th scope="col">Communities</th>
                                    <th scope="col">Status</th>
                                    <th scope="col" className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {directoryLoading ? (
                                    <tr><td colSpan={7} className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                        <span className="ms-2 text-secondary-light">Loading…</span>
                                    </td></tr>
                                ) : persons.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center py-4 text-secondary-light">
                                        <Icon icon="mdi:account-search-outline" className="d-block mx-auto mb-1 text-2xl" />
                                        No employees found
                                    </td></tr>
                                ) : (
                                    persons.map((p, index) => {
                                        const k = personKey(p);
                                        const names = (p.societies || []).map((s) => s.society?.name).filter(Boolean);
                                        const total = p.society_count ?? (p.societies || []).length;
                                        return (
                                            <tr key={k}>
                                                <td>
                                                    <div className="form-check style-check d-flex align-items-center">
                                                        <input className="form-check-input" type="checkbox"
                                                            checked={selectedKeys.includes(k)} onChange={() => toggleOne(k)} />
                                                    </div>
                                                </td>
                                                <td className="text-xs">{startIndex + index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="emp-avatar">{initials(p)}</span>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div className="emp-name">{employeeFullName(p)}</div>
                                                            <div className="emp-sub">{p.email || '—'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{p.phone || '—'}</td>
                                                <td>
                                                    <div className="d-flex flex-wrap align-items-center gap-1">
                                                        {names.slice(0, 2).map((n, i) => (
                                                            <span key={i} className="emp-chip bg-primary-50 text-primary-600" title={n}>{n}</span>
                                                        ))}
                                                        {total > 2 && (
                                                            <Tooltip title={names.slice(2).join(', ')} placement="top" arrow>
                                                                <span className="emp-chip bg-neutral-200 text-secondary-light">+{total - 2}</span>
                                                            </Tooltip>
                                                        )}
                                                        {total === 0 && <span className="text-xs text-secondary-light">—</span>}
                                                    </div>
                                                </td>
                                                <td>{renderStatus(activeCount(p), total)}</td>
                                                <td className="text-end">
                                                    <div className="d-inline-flex align-items-center gap-1">
                                                        {(() => {
                                                            const reason = p.user_id == null
                                                                ? 'No login account — nothing to change'
                                                                : String(p.user_id) === String(selfUserId)
                                                                    ? 'Change your own password from your profile'
                                                                    : null;
                                                            return (
                                                                <Tooltip title={reason || 'Change password'} placement="top" arrow>
                                                                    <span>
                                                                        <button onClick={() => setPasswordPerson(p)} disabled={!!reason}
                                                                            className="emp-icon-btn">
                                                                            <Icon icon="solar:key-minimalistic-linear" />
                                                                        </button>
                                                                    </span>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                        <Tooltip title="Manage communities & roles" placement="top" arrow>
                                                            <button onClick={() => openManage(p)} className="emp-icon-btn">
                                                                <Icon icon="solar:settings-linear" />
                                                            </button>
                                                        </Tooltip>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="card-footer">
                    <PaginationLimit
                        currentPage={page}
                        totalRecords={totalRecords}
                        limit={limit}
                        handleLimitChange={(e) => { setLimit(Number(e.target.value) || DEFAULT_LIMIT); setPage(1); }}
                        handlePageChange={(pg) => { if (pg >= 1) setPage(pg); }}
                    />
                </div>
            </div>

            {/* Modals */}
            <EmployeeFormModal
                open={formState.open}
                mode={formState.mode}
                employee={formState.employee}
                societyOptions={societyOptions}
                typeOptions={typeOptions}
                submitting={createLoading || updateLoading}
                onClose={closeForm}
                onSubmit={handleFormSubmit}
            />

            <AddFromExistingModal
                open={addExistingOpen}
                societyOptions={societyOptions}
                typeOptions={typeOptions}
                initialEmail={addExistingEmail}
                onClose={() => { setAddExistingOpen(false); setAddExistingEmail(''); }}
                onLinked={() => { setAddExistingOpen(false); setAddExistingEmail(''); refreshAll(); }}
            />

            <BulkReassignModal
                open={reassignOpen}
                count={selectedEmployeeIds.length}
                societyOptions={societyOptions}
                submitting={bulkLoading}
                onClose={() => setReassignOpen(false)}
                onConfirm={handleBulkReassign}
            />

            <ChangePasswordModal
                open={!!passwordPerson}
                person={passwordPerson}
                onClose={() => setPasswordPerson(null)}
            />

            <EmployeeSocietiesModal
                open={!!managePerson}
                person={managePerson}
                societyOptions={societyOptions}
                typeOptions={typeOptions}
                onClose={() => setManagePerson(null)}
                onChanged={refreshAll}
                onEditRecord={handleEditRecord}
            />
        </>
    );
};

export default ManageEmployeeListLayer;
